import React, { useState, useEffect } from "react";

// internal imports
import { changePassword, checkRegistrationStatus } from "../../services/api";
import colors from "../../theme";
import "./ChangePassword.css";
import { formControlInputStyle } from "../../styles/formControlStyles";

// external imports
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import SnackbarComp from "../Snackbar/SnackbarComp";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import CircularProgress from "@mui/material/CircularProgress";

export default function ChangePassword() {
  const [error, setError] = useState(null);
  const [statusCode, setStatusCode] = useState(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const [oldPassword, setOldPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(true);
  const [showConfirmPassword, setShowConfirmPassword] = useState(true);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasOldPassword, setHasOldPassword] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);

  useEffect(() => {
    const fetchRegistrationStatus = async () => {
      try {
        const hasOldPwd = await checkRegistrationStatus();
        setHasOldPassword(hasOldPwd);
      } catch (error) {
        console.error("Failed to check registration status", error);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    fetchRegistrationStatus();
  }, []);
  const validatePassword = (password) => {
    const passwordRegex =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[~!@#$%^&*()_\-+=\[\]{}\\/|<>?])[A-Za-z\d~!@#$%^&*()_\-+=\[\]{}\\/|<>?]{8,}$/; // eslint-disable-line
    return passwordRegex.test(password);
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();

    const newErrors = {};

    if (hasOldPassword) {
      if (!oldPassword) {
        newErrors.oldPassword = "Old password is required";
      } else if (!validatePassword(oldPassword)) {
        newErrors.oldPassword =
          "Password must be at least 8 characters long, contain an uppercase letter, a number, and a special character";
      }
    }

    if (!password) {
      newErrors.password = "New password is required";
    } else if (!validatePassword(password)) {
      newErrors.password =
        "Password must be at least 6 characters long, contain an uppercase letter, a number, and a special character";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      setIsLoading(true);

      try {
        const response = await changePassword({
          hasOldPassword,
          oldPassword,
          password,
          confirmPassword,
        });
        setError(response?.message || "An unknown error occurred");
        setStatusCode(response?.status || 500);
        setOpenSnackbar(true);
      } catch (error) {
        setError(error.message || "An unknown error occurred");
        setStatusCode(error.status || 500);
        setOpenSnackbar(true);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show);
  const handleMouseDownOldPassword = (event) => event.preventDefault();
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => event.preventDefault();
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);
  const handleMouseDownConfirmPassword = (event) => event.preventDefault();

  if (isCheckingStatus) {
    return (
      <Box
        component="main"
        sx={{ flex: 1, py: 6, px: 4, bgcolor: colors.mainContentBgColor }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <CircularProgress />
      </Box>
    );
  }
  return (
    <>
      <Box
        component="main"
        sx={{ flex: 1, py: 6, px: 4, bgcolor: colors.mainContentBgColor }}
      >
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 6,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              component="form"
              onSubmit={handleChangePassword}
              sx={{ mt: 1 }}
              noValidate
            >
              {hasOldPassword && (
                <FormControl
                  required
                  fullWidth
                  variant="outlined"
                  error={!!errors.oldPassword}
                  sx={{ ...formControlInputStyle, marginBottom: 2 }}
                >
                  <InputLabel htmlFor="oldPassword">Old Password</InputLabel>
                  <OutlinedInput
                    id="oldPassword"
                    name="oldPassword"
                    type={!showOldPassword ? "text" : "password"}
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle old password visibility"
                          onClick={handleClickShowOldPassword}
                          onMouseDown={handleMouseDownOldPassword}
                          edge="end"
                        >
                          {showOldPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="Old Password"
                    sx={{ borderRadius: "20px", height: "3.5rem" }}
                  />
                  {errors.oldPassword && (
                    <Typography variant="body2" color="error">
                      {errors.oldPassword}
                    </Typography>
                  )}
                </FormControl>
              )}
              <FormControl
                required
                fullWidth
                variant="outlined"
                error={!!errors.password}
                sx={{ ...formControlInputStyle, marginBottom: 2 }}
              >
                <InputLabel htmlFor="password">New Password</InputLabel>
                <OutlinedInput
                  id="password"
                  name="password"
                  type={!showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New Password"
                  sx={{ borderRadius: "20px", height: "3.5rem" }}
                />
                {errors.password && (
                  <Typography variant="body2" color="error">
                    {errors.password}
                  </Typography>
                )}
              </FormControl>
              <FormControl
                required
                fullWidth
                variant="outlined"
                error={!!errors.confirmPassword || !!errors.password}
                sx={formControlInputStyle}
              >
                <InputLabel htmlFor="confirmPassword">
                  Confirm Password
                </InputLabel>
                <OutlinedInput
                  id="confirmPassword"
                  name="confirmPassword"
                  type={!showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm Password"
                  sx={{ borderRadius: "20px", height: "3.5rem" }}
                />
                {errors.confirmPassword && (
                  <Typography variant="body2" color="error">
                    {errors.confirmPassword}
                  </Typography>
                )}
              </FormControl>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  fontWeight: "bold",
                  borderRadius: "20px",
                  height: "3.5rem",
                  marginTop: "20px",
                  backgroundColor: colors.primary,
                  "&:hover": {
                    backgroundColor: colors.hoverButton,
                  },
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : "Change Password"}
              </Button>
            </Box>
          </Box>
        </Container>
        <SnackbarComp
          open={openSnackbar}
          onClose={handleCloseSnackbar}
          errorMessage={error || Object.values(errors).join(". ")}
          statusCode={statusCode}
        />
      </Box>
    </>
  );
}
